import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Clock, X, Search } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { posApi } from '@/services/api';
import { useShiftStore } from '@/store/shiftStore';

import BankAccountSelect from '@/components/common/BankAccountSelect';
import { formatMoney, getCurrencySymbol } from '@/utils/currency';
import { translateInvoiceStatus, translatePaymentMethod } from '@/utils/translateStatus';

interface PendingInvoice {
  name: string;
  customer: string;
  customer_name: string;
  invoice_date: string;
  status: string;
  net_total: number;
  paid_amount: number;
  balance_amount: number;
  write_off_amount?: number;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  maxWriteOff?: number;
}

export default function PendingInvoicesModal({ isOpen, onClose, maxWriteOff = 50 }: Props) {
  const { t } = useTranslation();
  const [invoiceNo, setInvoiceNo] = useState('');

  const [appliedFilters, setAppliedFilters] = useState<{
    invoice_no?: string;
    status?: string;
  }>({
    status: 'Unpaid'
  });

  const [payTarget, setPayTarget] = useState<PendingInvoice | null>(null);
  const [amount, setAmount] = useState('');
  const [writeOff, setWriteOff] = useState('');
  const [method, setMethod] = useState('Cash');
  const [bankAccount, setBankAccount] = useState('');
  const [ref, setRef] = useState('');
  const qc = useQueryClient();
  const openShift = useShiftStore((s) => s.openShift);
  const { data: banks = [] } = useQuery({
    queryKey: ['bankAccounts'],
    queryFn: async () => {
      const res = await posApi.getBankAccounts();
      return res.data.message || [];
    },
    enabled: isOpen,
  });

  const { data: invoices = [], isLoading, isFetching } = useQuery({
    queryKey: ['pendingInvoices', appliedFilters],
    queryFn: async () => {
      const params: { invoice_no?: string; status?: string } = {};
      if (appliedFilters.invoice_no) params.invoice_no = appliedFilters.invoice_no;
      if (appliedFilters.status) params.status = appliedFilters.status;
      const res = await posApi.getPendingInvoices(params);
      return (res.data.message || []) as PendingInvoice[];
    },
    enabled: isOpen,
    refetchInterval: isOpen ? 60_000 : false,
  });

  const payMutation = useMutation({
    mutationFn: async () => {
      if (!payTarget) return;
      const payAmt = Number(amount) || 0;
      const woAmt = Number(writeOff) || 0;
      if (woAmt > maxWriteOff) {
        throw new Error(`Write off cannot exceed ${formatMoney(maxWriteOff)}`);
      }
      if (payAmt > 0 && method === 'Bank' && !bankAccount) {
        throw new Error('Select a bank');
      }
      const bank = banks.find((b: { name: string; bank_name: string }) => b.name === bankAccount);
      await posApi.addSalePayment(payTarget.name, {
        amount: payAmt,
        write_off_amount: woAmt,
        payment_method: method,
        bank_account: method === 'Bank' ? bankAccount : '',
        bank_name: method === 'Bank' ? (bank?.bank_name || '') : '',
        reference_no: ref,
        date: new Date().toISOString().slice(0, 10),
        account: method === 'Cash'
          ? (openShift?.pocket_name || '')
          : (bank?.bank_name || ''),
        ...(openShift ? { pos_shift: openShift.name } : {}),
      });
    },
    onSuccess: () => {
      toast.success('Payment recorded');
      qc.invalidateQueries({ queryKey: ['pendingInvoices'] });
      setPayTarget(null);
      setAmount('');
      setWriteOff('');
      setRef('');
      setMethod('Cash');
      setBankAccount('');
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
        || (err as { message?: string })?.message;
      toast.error(msg || 'Failed to add payment');
    },
  });

  const handleSearch = () => {
    setAppliedFilters({
      invoice_no: invoiceNo.trim() || undefined,
      status: 'Unpaid',
    });
  };

  const handleClearFilters = () => {
    setInvoiceNo('');
    setAppliedFilters({
      status: 'Unpaid'
    });
  };

  const handleClose = () => {
    setPayTarget(null);
    setAmount('');
    setWriteOff('');
    setRef('');
    onClose();
  };

  const openPay = (inv: PendingInvoice) => {
    setPayTarget(inv);
    setAmount(String(inv.balance_amount));
    setWriteOff('');
  };

  const payAmt = Number(amount) || 0;
  const woAmt = Math.min(Number(writeOff) || 0, maxWriteOff);
  const canSavePay = payTarget && (payAmt > 0 || woAmt > 0) && woAmt <= maxWriteOff;

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-100 shrink-0">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-600" />
              <h3 className="font-semibold text-gray-900 text-lg">{t('pendingInvoices.title')}</h3>
              {invoices.length > 0 && (
                <span className="bg-amber-100 text-amber-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                  {invoices.length}
                </span>
              )}
            </div>
            <button type="button" onClick={handleClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          <div className="px-4 py-3 border-b border-gray-100 shrink-0 space-y-3">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('pendingInvoices.invoiceNo')}</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="input-field text-sm py-1.5 flex-1"
                    placeholder={t('pendingInvoices.invoicePlaceholder')}
                    value={invoiceNo}
                    onChange={(e) => setInvoiceNo(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSearch();
                      }
                    }}
                  />
                  <button type="button" onClick={handleSearch} className="btn-primary px-3 py-1.5 text-sm shrink-0 flex items-center gap-1">
                    <Search className="w-3.5 h-3.5" /> {t('common.search')}
                  </button>
                </div>
              </div>
            </div>
            {(appliedFilters.invoice_no) && (
              <button type="button" onClick={handleClearFilters} className="text-xs text-gray-500 hover:text-gray-700">
                {t('pendingInvoices.clearFilters')}
              </button>
            )}
          </div>

          <div className="flex-1 overflow-auto p-4 space-y-1">
            {isLoading || isFetching ? (
              <p className="text-sm text-gray-500 py-8 text-center">{t('pendingInvoices.loading')}</p>
            ) : invoices.length === 0 ? (
              <p className="text-sm text-gray-500 py-8 text-center">{t('pendingInvoices.empty')}</p>
            ) : (
              invoices.map((inv) => (
                <div
                  key={inv.name}
                  className="flex items-center justify-between gap-2 border-b border-gray-100 py-2 last:border-0 hover:bg-gray-50 px-2 -mx-2 rounded-lg"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900 text-sm truncate">{inv.name}</p>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                        inv.status === 'Paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {translateInvoiceStatus(inv.status, t)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 truncate mt-0.5">
                      {inv.customer_name}
                      {inv.invoice_date && (
                        <> · {format(new Date(inv.invoice_date), 'dd MMM yyyy')}</>
                      )}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`font-semibold text-sm ${inv.status === 'Paid' ? 'text-gray-900' : 'text-amber-700'}`}>
                      {formatMoney(Number(inv.status === 'Paid' ? inv.net_total : inv.balance_amount))}
                    </p>
                    {inv.status !== 'Paid' && (
                      <button
                        type="button"
                        onClick={() => openPay(inv)}
                        className="text-[11px] text-primary-600 font-medium hover:underline mt-0.5"
                      >
                        {t('pendingInvoices.payBalance')}
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {payTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-5 space-y-4">
            <div className="flex justify-between items-start">
              <div>
                <h4 className="font-semibold text-gray-900">{t('pendingInvoices.addPayment')}</h4>
                <p className="text-sm text-gray-500">{payTarget.name} · {payTarget.customer_name}</p>
              </div>
              <button type="button" onClick={() => setPayTarget(null)} className="p-1 text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-amber-700">
              {t('pendingInvoices.balance')} {formatMoney(Number(payTarget.balance_amount))}
              {payTarget.invoice_date && (
                <span className="text-gray-500"> · {format(new Date(payTarget.invoice_date), 'dd MMM yyyy')}</span>
              )}
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('pendingInvoices.method')}</label>
              <select
                className="input-field"
                value={method}
                onChange={(e) => {
                  const next = e.target.value;
                  setMethod(next);
                  if (next === 'Bank' && !bankAccount && banks.length) {
                    const def = banks.find((b: { is_default: number }) => b.is_default) || banks[0];
                    setBankAccount(def.name);
                  }
                  if (next !== 'Bank') setBankAccount('');
                }}
              >
                <option value="Cash">{translatePaymentMethod('Cash', t)}</option>
                <option value="Bank">{translatePaymentMethod('Bank', t)}</option>
              </select>
            </div>
            {method === 'Bank' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bank *</label>
                <BankAccountSelect
                  value={bankAccount}
                  onChange={setBankAccount}
                  banks={banks}
                  required
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount ({getCurrencySymbol()})</label>
              <input
                type="number"
                min={0}
                step="0.01"
                max={payTarget.balance_amount}
                className="input-field"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Write off ({getCurrencySymbol()})</label>
              <input
                type="number"
                min={0}
                step="0.01"
                max={Math.min(maxWriteOff, payTarget.balance_amount)}
                className="input-field"
                value={writeOff}
                onChange={(e) => setWriteOff(e.target.value)}
                placeholder="0"
              />
              <p className="text-xs text-gray-500 mt-1">Max: {formatMoney(maxWriteOff)} (from Settings)</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reference (optional)</label>
              <input type="text" className="input-field" value={ref} onChange={(e) => setRef(e.target.value)} />
            </div>
            {payAmt + woAmt > 0 && payAmt + woAmt < Number(payTarget.balance_amount) - 0.01 && (
              <p className="text-xs text-amber-700">
                Remaining after save: {formatMoney((Number(payTarget.balance_amount) - payAmt - woAmt))}
              </p>
            )}
            <button
              type="button"
              disabled={payMutation.isPending || !canSavePay}
              onClick={() => payMutation.mutate()}
              className="btn-primary w-full py-2.5 disabled:opacity-50"
            >
              {payMutation.isPending ? 'Saving...' : 'Save Payment'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
